import { useState, useEffect } from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { Skeleton } from './ui/skeleton';
import { Badge } from './ui/badge';
import { X } from 'lucide-react';
import axios from 'axios';
import { getToken } from '../lib/auth';
import { badgeUrl, playerUrl, wcPlayerUrl } from '../lib/assetUrls';
import { isWcTeam } from '../lib/wcTeams';

const API_URL      = process.env.REACT_APP_BACKEND_URL;

function authHeaders() { return { Authorization: `Bearer ${getToken()}` }; }

// Nationality adjective → ISO 3166-1 alpha-2 (lowercase) for flagcdn.com
// UK nations use flagcdn's subdivision codes
const NAT_TO_ISO = {
    'english': 'gb-eng', 'scottish': 'gb-sct', 'welsh': 'gb-wls',
    'northern irish': 'gb-nir', 'irish': 'ie',
    'norwegian': 'no', 'swedish': 'se', 'danish': 'dk', 'finnish': 'fi', 'icelandic': 'is',
    'german': 'de', 'austrian': 'at', 'swiss': 'ch', 'dutch': 'nl', 'belgian': 'be',
    'french': 'fr', 'spanish': 'es', 'portuguese': 'pt', 'italian': 'it', 'greek': 'gr',
    'turkish': 'tr', 'cypriot': 'cy', 'maltese': 'mt',
    'polish': 'pl', 'czech': 'cz', 'slovak': 'sk', 'hungarian': 'hu', 'romanian': 'ro',
    'bulgarian': 'bg', 'croatian': 'hr', 'serbian': 'rs', 'slovenian': 'si', 'bosnian': 'ba',
    'montenegrin': 'me', 'albanian': 'al', 'macedonian': 'mk', 'kosovan': 'xk',
    'latvian': 'lv', 'lithuanian': 'lt', 'estonian': 'ee', 'moldovan': 'md',
    'russian': 'ru', 'ukrainian': 'ua', 'belarusian': 'by', 'georgian': 'ge',
    'armenian': 'am', 'azerbaijani': 'az', 'kazakhstani': 'kz',
    'luxembourgish': 'lu', 'luxembourger': 'lu',
    'brazilian': 'br', 'argentinian': 'ar', 'uruguayan': 'uy', 'colombian': 'co',
    'chilean': 'cl', 'peruvian': 'pe', 'ecuadorian': 'ec', 'paraguayan': 'py',
    'venezuelan': 've', 'bolivian': 'bo', 'mexican': 'mx', 'costa rican': 'cr',
    'panamanian': 'pa', 'honduran': 'hn', 'guatemalan': 'gt', 'salvadoran': 'sv',
    'american': 'us', 'canadian': 'ca', 'jamaican': 'jm', 'trinidadian': 'tt',
    'barbadian': 'bb', 'haitian': 'ht', 'cuban': 'cu',
    'senegalese': 'sn', 'nigerian': 'ng', 'ghanaian': 'gh', 'ivorian': 'ci',
    'cameroonian': 'cm', 'malian': 'ml', 'guinean': 'gn', 'gambian': 'gm',
    'sierra leonean': 'sl', 'liberian': 'lr', 'togolese': 'tg', 'beninese': 'bj',
    'burkinabe': 'bf', 'congolese': 'cd', 'angolan': 'ao', 'mozambican': 'mz',
    'tanzanian': 'tz', 'kenyan': 'ke', 'ugandan': 'ug', 'rwandan': 'rw',
    'ethiopian': 'et', 'somali': 'so', 'sudanese': 'sd', 'zambian': 'zm',
    'zimbabwean': 'zw', 'south african': 'za', 'namibian': 'na', 'botswanan': 'bw',
    'moroccan': 'ma', 'algerian': 'dz', 'tunisian': 'tn', 'egyptian': 'eg',
    'libyan': 'ly', 'cape verdean': 'cv', 'capeverdean': 'cv', 'mauritanian': 'mr',
    'guinea-bissauan': 'gw', 'equatorial guinean': 'gq',
    'japanese': 'jp', 'south korean': 'kr', 'korean': 'kr', 'chinese': 'cn',
    'taiwanese': 'tw', 'australian': 'au', 'new zealander': 'nz',
    'iranian': 'ir', 'iraqi': 'iq', 'saudi arabian': 'sa', 'emirati': 'ae',
    'qatari': 'qa', 'kuwaiti': 'kw', 'bahraini': 'bh', 'omani': 'om', 'yemeni': 'ye',
    'jordanian': 'jo', 'lebanese': 'lb', 'syrian': 'sy', 'israeli': 'il', 'palestinian': 'ps',
    'indian': 'in', 'pakistani': 'pk', 'bangladeshi': 'bd', 'sri lankan': 'lk',
    'indonesian': 'id', 'thai': 'th', 'vietnamese': 'vn', 'philippine': 'ph',
    'malaysian': 'my', 'singaporean': 'sg',
};

function getFlagUrl(nationality) {
    if (!nationality) return null;
    const key = nationality.toLowerCase().trim();
    const iso = NAT_TO_ISO[key];
    if (!iso) return null;
    return `https://flagcdn.com/32x24/${iso}.png`;
}

function PlayerPhoto({ playerId, playerName, teamId, size = 'lg' }) {
    const [err, setErr] = useState(false);
    const dim      = size === 'lg' ? 'w-24 h-24' : 'w-12 h-12';
    const textSize = size === 'lg' ? 'text-3xl' : 'text-lg';

    if (playerId && playerId !== '0' && !err) {
        // Club team → club photo. International team → international photo.
        // teamId here is always the specific competition's team (club or
        // country), so this is never ambiguous.
        const src = isWcTeam(teamId) ? wcPlayerUrl(playerId) : playerUrl(playerId);
        return (
            <img
                src={src}
                alt={playerName}
                className={`${dim} rounded-full object-cover object-top border-4 border-white/20 flex-shrink-0`}
                onError={() => setErr(true)}
            />
        );
    }
    return (
        <div className={`${dim} rounded-full bg-white/10 border-4 border-white/20 flex items-center justify-center flex-shrink-0`}>
            <span className={`${textSize} font-bold text-white/60`}>
                {(playerName || '?')[0].toUpperCase()}
            </span>
        </div>
    );
}

function ClubBadge({ teamId, teamName }) {
    const [err, setErr] = useState(false);
    if (!err && teamId) {
        return (
            <img
                src={badgeUrl(teamId)}
                alt={teamName}
                className="w-8 h-8 object-contain flex-shrink-0"
                onError={() => setErr(true)}
            />
        );
    }
    return (
        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
            <span className="text-xs font-bold text-muted-foreground">{(teamName || '?')[0]}</span>
        </div>
    );
}

function StatChip({ label, value }) {
    if (!value) return null;
    return (
        <div className="flex flex-col items-center gap-0.5 bg-muted/50 rounded-xl px-3 py-2 min-w-0">
            <span className="text-xs text-muted-foreground font-medium whitespace-nowrap">{label}</span>
            <span className="text-sm font-bold text-foreground text-center leading-tight">{value}</span>
        </div>
    );
}

// ── Season stats table — shared between both modal layouts ──────────────────
function SeasonStatsTable({ stats, statsLoading, dark = false }) {
    const headerCls = dark
        ? 'grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 px-3 py-1.5 bg-white/5 text-xs font-semibold text-white/40'
        : 'grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 px-3 py-1.5 bg-muted/50 text-xs font-semibold text-muted-foreground';
    const rowCls = dark
        ? 'grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 px-3 py-2 border-t border-white/8 text-xs items-center'
        : 'grid grid-cols-[1fr_auto_auto_auto_auto_auto] gap-x-3 px-3 py-2 border-t text-xs items-center';
    const wrapCls = dark ? 'border border-white/10 rounded-xl overflow-hidden' : 'border rounded-xl overflow-hidden';
    const seasonCls = dark ? 'font-medium truncate text-white/90' : 'font-medium truncate';
    const compCls   = dark ? 'text-white/40' : 'text-muted-foreground';

    if (statsLoading) {
        return (
            <div className="space-y-2">
                {[1, 2, 3].map(i => <Skeleton key={i} className={`h-9 rounded-lg ${dark ? 'bg-white/10' : ''}`} />)}
            </div>
        );
    }
    if (!stats || stats.length === 0) {
        return <p className={`text-xs text-center py-3 ${dark ? 'text-white/40' : 'text-muted-foreground'}`}>No stats available.</p>;
    }
    return (
        <div className={wrapCls}>
            <div className={headerCls}>
                <span>Season</span>
                <span className="text-center w-7">App</span>
                <span className="text-center w-7">⚽</span>
                <span className="text-center w-7">🎯</span>
                <span className="text-center w-7">🟨</span>
                <span className="text-center w-7">🟥</span>
            </div>
            {stats.slice(0, 4).map((s, i) => (
                <div key={i} className={rowCls}>
                    <div className="min-w-0">
                        <p className={seasonCls}>{s.season}</p>
                        <p className={compCls}>{s.competition}</p>
                    </div>
                    <span className={`text-center w-7 tabular-nums ${dark ? 'text-white/70' : ''}`}>{s.appearances ?? '–'}</span>
                    <span className={`text-center w-7 tabular-nums font-semibold ${dark ? 'text-emerald-400' : ''}`}>{s.goals ?? '–'}</span>
                    <span className={`text-center w-7 tabular-nums ${dark ? 'text-sky-400' : ''}`}>{s.assists ?? '–'}</span>
                    <span className={`text-center w-7 tabular-nums ${dark ? 'text-amber-400' : ''}`}>{s.yellow_cards ?? '–'}</span>
                    <span className={`text-center w-7 tabular-nums ${dark ? 'text-red-400' : ''}`}>{s.red_cards ?? '–'}</span>
                </div>
            ))}
        </div>
    );
}

export function PlayerProfileModal({ playerId, playerName, teamId, compId, layout, onClose }) {
    const [profile, setProfile] = useState(null);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(true);
    const [wcStats, setWcStats] = useState(null);
    const [wcStatsLoading, setWcStatsLoading] = useState(false);
    const [imgErr, setImgErr] = useState(false);

    // Use the card (landscape) layout when explicitly requested or for WC squad players
    const isWc      = isWcTeam(teamId);
    const useCard   = layout === 'card' || isWc;

    useEffect(() => {
        if (!playerId || playerId === '0') {
            setLoading(false);
            setStatsLoading(false);
            return;
        }
        setLoading(true);
        setStatsLoading(true);
        setProfile(null);
        setStats(null);
        setWcStats(null);
        setImgErr(false);
        axios.get(`${API_URL}/api/football/hub/player/${playerId}`, { headers: authHeaders() })
            .then(r => setProfile(r.data))
            .catch(() => setProfile({ error: true }))
            .finally(() => setLoading(false));
        const statsParams = compId ? `?comp_id=${compId}` : '';
        axios.get(`${API_URL}/api/football/hub/player/${playerId}/stats${statsParams}`, { headers: authHeaders() })
            .then(r => setStats(r.data.stats || []))
            .catch(() => setStats([]))
            .finally(() => setStatsLoading(false));
        // For WC players, also fetch tournament stats
        if (isWc) {
            setWcStatsLoading(true);
            axios.get(`${API_URL}/api/football/worldcup/player/${playerId}/tournament-stats`, { headers: authHeaders() })
                .then(r => setWcStats(r.data))
                .catch(() => setWcStats({ available: false }))
                .finally(() => setWcStatsLoading(false));
        }
    }, [playerId, compId, isWc]);

    const flagUrl = getFlagUrl(profile?.nationality);
    const club    = profile?.clubs?.[0];
    const displayName = profile?.full_name || playerName;
    const resolvedTeamId = teamId || profile?.clubs?.[0]?.team_id;

    // ── Card layout: image LEFT (name/position overlaid), bio+stats RIGHT (dark) ──
    if (useCard) {
        const imgSrc = !imgErr && playerId && playerId !== '0'
            ? (isWc ? wcPlayerUrl(playerId) : playerUrl(playerId))
            : null;

        return (
            <Dialog open onOpenChange={onClose}>
                <DialogContent className="p-0 overflow-hidden max-w-lg w-full border-0 rounded-2xl bg-[#070714]">
                    <button
                        onClick={onClose}
                        className="absolute top-3 right-3 z-20 text-white/40 hover:text-white/80 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    <div className="flex min-h-[380px]">
                        {/* ── LEFT panel ── */}
                        <div className={`flex-shrink-0 relative bg-gradient-to-b from-[#0d0d20] to-[#070714] ${isWc ? 'w-[42%]' : 'w-[38%]'}`}>
                            {isWc ? (
                                /* WC: full-panel high-res image */
                                <>
                                    {imgSrc ? (
                                        <img
                                            src={imgSrc}
                                            alt={displayName}
                                            className="absolute inset-0 w-full h-full object-cover object-top"
                                            onError={() => setImgErr(true)}
                                        />
                                    ) : (
                                        <div className="absolute inset-0 flex items-center justify-center">
                                            <span className="text-6xl font-black text-white/10">
                                                {(displayName || '?')[0].toUpperCase()}
                                            </span>
                                        </div>
                                    )}
                                    <div className="absolute bottom-0 inset-x-0 h-28 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
                                    <div className="absolute inset-y-0 right-0 w-6 bg-gradient-to-r from-transparent to-[#070714] pointer-events-none" />
                                    {/* Name + position overlaid at bottom */}
                                    <div className="absolute bottom-0 inset-x-0 px-3 pb-3">
                                        {loading ? (
                                            <div className="space-y-1">
                                                <Skeleton className="h-4 w-24 bg-white/20" />
                                                <Skeleton className="h-3 w-16 bg-white/20" />
                                            </div>
                                        ) : (
                                            <>
                                                <p className="text-white font-bold text-sm leading-tight drop-shadow">{displayName}</p>
                                                {profile?.position && (
                                                    <Badge className="mt-1 bg-primary/30 text-primary border-primary/40 text-[10px] font-semibold px-1.5 py-0">
                                                        {profile.position}
                                                    </Badge>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </>
                            ) : (
                                /* Non-WC: circular photo centred, details below */
                                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-3">
                                    <PlayerPhoto
                                        playerId={playerId}
                                        playerName={displayName}
                                        teamId={resolvedTeamId}
                                        size="lg"
                                    />
                                    {!loading && (
                                        <div className="text-center space-y-1">
                                            <p className="text-white font-bold text-sm leading-tight">{displayName}</p>
                                            {profile?.position && (
                                                <Badge className="bg-primary/20 text-primary border-primary/30 text-[10px] font-semibold px-1.5 py-0">
                                                    {profile.position}
                                                </Badge>
                                            )}
                                        </div>
                                    )}
                                    {loading && (
                                        <div className="space-y-1 items-center flex flex-col">
                                            <Skeleton className="h-4 w-24 bg-white/10" />
                                            <Skeleton className="h-3 w-16 bg-white/10" />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* ── RIGHT: club + bio chips + stats (all dark) ── */}
                        <div className="flex-1 flex flex-col min-w-0 bg-[#070714]">
                            {/* Club + nationality */}
                            <div className="px-4 pt-4 pb-3">
                                {loading ? (
                                    <Skeleton className="h-8 w-full bg-white/10 rounded-lg" />
                                ) : club ? (
                                    <div className="flex items-center gap-2">
                                        <ClubBadge teamId={club.team_id} teamName={club.name} />
                                        <div className="min-w-0 flex-1">
                                            <p className="text-xs font-semibold text-white/80 truncate leading-tight">{club.name}</p>
                                            <div className="flex items-center gap-1 mt-0.5">
                                                {flagUrl && (
                                                    <img src={flagUrl} alt={profile?.nationality}
                                                        className="w-3.5 h-2.5 object-cover rounded-sm flex-shrink-0"
                                                        onError={e => e.target.style.display = 'none'} />
                                                )}
                                                <span className="text-[10px] text-white/40">{profile?.nationality}</span>
                                            </div>
                                        </div>
                                        {club.squad_number && (
                                            <span className="text-lg font-black text-white/15 tabular-nums flex-shrink-0">
                                                #{club.squad_number}
                                            </span>
                                        )}
                                    </div>
                                ) : null}

                                {/* Bio chips */}
                                {!loading && (
                                    <div className="grid grid-cols-2 gap-1.5 mt-3">
                                        {[
                                            { label: 'Age',    value: profile?.age ? `${profile.age} yrs` : null },
                                            { label: 'DOB',    value: profile?.dob },
                                            { label: 'Height', value: profile?.height?.replace(' Metres', 'm') },
                                            { label: 'Weight', value: profile?.weight?.replace(' Kilograms', 'kg') },
                                        ].map(({ label, value }) => value ? (
                                            <div key={label} className="bg-white/5 rounded-lg py-1.5 px-2">
                                                <p className="text-[9px] text-white/30 font-semibold uppercase tracking-wide">{label}</p>
                                                <p className="text-xs font-bold text-white/75 leading-tight">{value}</p>
                                            </div>
                                        ) : null)}
                                    </div>
                                )}
                            </div>

                            {/* Divider */}
                            <div className="mx-4 border-t border-white/8" />

                            {/* Stats: WC tournament stats take priority for WC players */}
                            <div className="px-4 pt-3 pb-4 flex-1 overflow-y-auto">
                                {wcStatsLoading && (
                                    <div className="flex items-center gap-2 py-2">
                                        <div className="w-3 h-3 border border-white/20 border-t-white/50 rounded-full animate-spin flex-shrink-0" />
                                        <p className="text-white/30 text-[10px]">Loading tournament stats…</p>
                                    </div>
                                )}
                                {!wcStatsLoading && wcStats?.available && (() => {
                                    const ts = wcStats.stats;
                                    const rCol = ts.rating >= 8 ? '#10b981' : ts.rating >= 7 ? '#84cc16' : ts.rating >= 6 ? '#f59e0b' : '#ef4444';
                                    const fmtDuel = (won, pct) =>
                                        won != null && pct != null ? `${won} (${Math.round(pct)}%)` : (won ?? null);
                                    const chips = [
                                        { key: 'goals',      label: 'Goals',        val: ts.scoring?.goals,                                                hi: '#10b981' },
                                        { key: 'assists',    label: 'Assists',       val: ts.scoring?.assists,                                             hi: '#38bdf8' },
                                        { key: 'big_ch',     label: 'Big chances',  val: ts.scoring?.big_chances_created,                                 hi: '#f59e0b' },
                                        { key: 'shots',      label: 'Shots',        val: ts.shooting?.total_shots,                                        hi: null },
                                        { key: 'shots_ot',   label: 'On target',    val: ts.shooting?.shots_on_target,                                    hi: null },
                                        { key: 'passes',     label: 'Passes',       val: ts.passing?.total_passes,                                        hi: null },
                                        { key: 'pass_acc',   label: 'Pass acc',     val: ts.passing?.pass_accuracy != null ? `${Number(ts.passing.pass_accuracy).toFixed(0)}%` : null, hi: null },
                                        { key: 'key_pass',   label: 'Key passes',   val: ts.passing?.key_passes,                                          hi: null },
                                        { key: 'crosses',    label: 'Acc. crosses', val: ts.passing?.accurate_crosses,                                    hi: null },
                                        { key: 'tackles',    label: 'Tackles',      val: ts.defending?.tackles,                                           hi: null },
                                        { key: 'intercepts', label: 'Intercepts',   val: ts.defending?.interceptions,                                     hi: null },
                                        { key: 'ground',     label: 'Ground duels', val: fmtDuel(ts.duels?.ground_duels_won, ts.duels?.ground_duels_won_percentage), hi: null },
                                        { key: 'aerial',     label: 'Aerial duels', val: fmtDuel(ts.duels?.aerial_duels_won, ts.duels?.aerial_duels_won_percentage), hi: null },
                                        { key: 'dribbles',   label: 'Dribbles',     val: ts.duels?.successful_dribbles,                                   hi: null },
                                        { key: 'yellow',     label: 'Yellow',       val: ts.discipline?.yellow_cards,                                     hi: '#eab308' },
                                        { key: 'red',        label: 'Red card',     val: ts.discipline?.red_cards,                                        hi: '#ef4444' },
                                    ].filter(c => c.val != null && c.val !== 0 && c.val !== '0%');
                                    return (
                                        <>
                                            <div className="flex items-center justify-between mb-2">
                                                <p className="text-[10px] font-bold uppercase tracking-widest text-white/30">🏆 World Cup 2026</p>
                                                <div className="flex items-center gap-1.5">
                                                    <span className="text-white/30 text-[10px]">{ts.appearances} app{ts.appearances !== 1 ? 's' : ''}</span>
                                                    {ts.rating > 0 && (
                                                        <span className="font-black text-xs px-1.5 py-0.5 rounded text-white"
                                                            style={{ background: rCol }}>{ts.rating.toFixed(1)}</span>
                                                    )}
                                                </div>
                                            </div>
                                            {chips.length > 0 ? (
                                                <div className="grid grid-cols-2 gap-1">
                                                    {chips.map(c => (
                                                        <div key={c.key} className="bg-white/5 rounded-lg py-1.5 px-2">
                                                            <p className="text-[9px] text-white/30 font-semibold uppercase tracking-wide">{c.label}</p>
                                                            <p className="text-sm font-bold leading-tight" style={{ color: c.hi || 'rgba(255,255,255,0.85)' }}>{c.val}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <p className="text-white/25 text-xs text-center py-3">No stats yet this tournament</p>
                                            )}
                                        </>
                                    );
                                })()}
                                {!wcStatsLoading && !wcStats?.available && (
                                    <>
                                        <p className="text-[10px] font-bold uppercase tracking-widest text-white/30 mb-2">
                                            Season Stats (All Comps)
                                        </p>
                                        <SeasonStatsTable stats={stats} statsLoading={statsLoading} dark />
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        );
    }

    // ── Standard (non-WC) layout ─────────────────────────────────────────────
    return (
        <Dialog open onOpenChange={onClose}>
            <DialogContent className="p-0 overflow-hidden max-w-sm w-full border-0 rounded-2xl">
                {/* Dark header */}
                <div className="bg-gradient-to-br from-[#0a0a1a] via-[#000028] to-[#0d0d1d] px-6 pt-6 pb-8 relative">
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/40 hover:text-white/80 transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>

                    {loading ? (
                        <div className="flex items-center gap-4">
                            <Skeleton className="w-24 h-24 rounded-full bg-white/10" />
                            <div className="space-y-2">
                                <Skeleton className="h-5 w-36 bg-white/10" />
                                <Skeleton className="h-4 w-24 bg-white/10" />
                            </div>
                        </div>
                    ) : profile?.error ? (
                        <p className="text-white/60 text-sm">Could not load profile.</p>
                    ) : (
                        <div className="flex items-center gap-4">
                            <PlayerPhoto playerId={playerId} playerName={displayName} teamId={resolvedTeamId} size="lg" />
                            <div className="min-w-0">
                                <h2 className="text-white font-bold text-lg leading-tight">{displayName}</h2>
                                {profile?.position && (
                                    <Badge className="mt-1 bg-primary/20 text-primary border-primary/30 text-xs font-semibold">
                                        {profile.position}
                                    </Badge>
                                )}
                                {profile?.nationality && (
                                    <div className="flex items-center gap-1.5 mt-2">
                                        {flagUrl && (
                                            <img
                                                src={flagUrl}
                                                alt={profile.nationality}
                                                className="w-5 h-4 object-cover rounded-sm"
                                                onError={e => e.target.style.display = 'none'}
                                            />
                                        )}
                                        <span className="text-sm text-white/60">{profile.nationality}</span>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </div>

                {/* Body */}
                {!loading && !profile?.error && (
                    <div className="px-5 py-4 space-y-4 bg-card">
                        {club && (
                            <div>
                                <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Club</p>
                                <div className="flex items-center gap-3 bg-muted/40 rounded-xl px-3 py-2.5">
                                    <ClubBadge teamId={club.team_id} teamName={club.name} />
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm leading-tight">{club.name}</p>
                                        {club.on_loan && <span className="text-xs text-amber-400 font-medium">On Loan</span>}
                                    </div>
                                    {club.squad_number && (
                                        <span className="text-2xl font-black text-muted-foreground/50 tabular-nums">
                                            #{club.squad_number}
                                        </span>
                                    )}
                                </div>
                            </div>
                        )}
                        <div>
                            <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Player Info</p>
                            <div className="grid grid-cols-2 gap-2">
                                <StatChip label="Age" value={profile?.age ? `${profile.age} yrs` : null} />
                                <StatChip label="Date of Birth" value={profile?.dob} />
                                <StatChip label="Height" value={profile?.height?.replace(' Metres', 'm')} />
                                <StatChip label="Weight" value={profile?.weight?.replace(' Kilograms', 'kg')} />
                            </div>
                        </div>
                    </div>
                )}

                {/* Season stats */}
                {!loading && !profile?.error && (
                    <div className="px-5 pb-5 bg-card">
                        <p className="text-xs text-muted-foreground font-semibold uppercase tracking-wide mb-2">Season Stats (All Comps)</p>
                        <SeasonStatsTable stats={stats} statsLoading={statsLoading} />
                    </div>
                )}

                {loading && (
                    <div className="px-5 py-4 space-y-3 bg-card">
                        <Skeleton className="h-16 w-full rounded-xl" />
                        <div className="grid grid-cols-2 gap-2">
                            {[1,2,3,4].map(i => <Skeleton key={i} className="h-12 rounded-xl" />)}
                        </div>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
